import { useEffect, useState } from "react";
import type { Contact, Conversation, MessageEvent } from "@veto/domain";
import { CONTENT_TYPE_ATTACHMENT } from "@veto/protocol";
import { getMeta } from "@veto/storage";
import { ModalShell } from "./ModalShell";

/**
 * Forward to one or more chats (phone parity). Text can go anywhere; files only to 1:1 chats
 * (groups don't carry files on either app). Locked chats and message requests aren't offered.
 */
export function ForwardModal({
  message,
  conversations,
  contacts,
  onClose,
  onForward,
}: {
  message: MessageEvent;
  conversations: Conversation[];
  contacts: Contact[];
  onClose: () => void;
  onForward: (targets: Conversation[]) => void;
}) {
  const isFile = message.contentType === CONTENT_TYPE_ATTACHMENT;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [lockedIds, setLockedIds] = useState<Set<string> | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let live = true;
    void Promise.all(
      conversations.map(async (c) => ((await getMeta(`chatlock.${c.id}`)) ? c.id : null)),
    ).then((ids) => {
      if (live) setLockedIds(new Set(ids.filter((x): x is string => !!x)));
    });
    return () => {
      live = false;
    };
  }, [conversations]);

  const q = query.trim().toLowerCase();
  const targets = conversations.filter((c) => {
    if (lockedIds === null || lockedIds.has(c.id) || c.requestPending) return false;
    if (isFile && c.type === "group") return false;
    if (c.type === "direct") {
      const contact = contacts.find((x) => x.id === c.contactId);
      if (!contact || contact.blockedAt || contact.requestState === "pending") return false;
    }
    return !q || c.title.toLowerCase().includes(q);
  });

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <ModalShell
      title="Forward to…"
      ariaLabel="Forward message"
      onClose={onClose}
      size="sheet"
      className="forward-modal"
      footer={
        <div className="actions-row">
          <button type="button" className="btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={picked.size === 0}
            onClick={() => onForward(conversations.filter((c) => picked.has(c.id)))}
          >
            {picked.size > 1 ? `Forward to ${picked.size} chats` : "Forward"}
          </button>
        </div>
      }
    >
      <input
        className="forward-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search chats"
        aria-label="Search chats"
      />
      {isFile && <p className="muted text-sm">Files can be forwarded to one-to-one chats.</p>}
      <div className="forward-list" role="listbox" aria-multiselectable="true">
        {targets.length === 0 && <p className="muted">No chat to forward to.</p>}
        {targets.map((c) => (
          <label key={c.id} className="forward-row">
            <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
            <span className="forward-title">{c.title}</span>
            {c.type === "group" && <span className="muted text-sm">Group</span>}
          </label>
        ))}
      </div>
    </ModalShell>
  );
}
